/**
 * Small presentation-only formatting helpers shared by the Maps UI.
 * No side effects; pure functions on plain data.
 */

/**
 * Human "edited N ago" string from an ISO timestamp, matching the mock-up's
 * relative-time copy ("2h ago", "Yesterday", "Jun 27"). Falls back to an empty
 * string on missing/invalid input rather than throwing.
 *
 * @param {string} iso - ISO-8601 timestamp
 * @returns {string}
 */
export function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const now = new Date();
  const diffMs = now - then;
  const diffMin = Math.round(diffMs / 60000);
  const diffHr = Math.round(diffMs / 3600000);
  const diffDay = Math.round(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;

  // Older than a week: absolute short date, dropping the year unless it differs.
  const sameYear = then.getFullYear() === now.getFullYear();
  return then.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/**
 * Integer with thousands separators (populations, household counts).
 * @param {number|null|undefined} n
 * @returns {string}
 */
export function formatNumber(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US');
}

/**
 * Human display label for an area — the group-card chip list and the
 * map's click-to-select popup both need the same "what is this area called"
 * text, so it lives here instead of being duplicated per component.
 *
 * @param {{area_type?: string, geoid?: string, label?: string}} area
 * @returns {string}
 */
export function areaLabel(area) {
  if (area.area_type === 'county' && area.geoid) {
    const name = area.label || area.name || 'County';
    // area.label already includes ", ST" from areaFromResolved; append the GEOID.
    return `${name} — ${area.geoid}`;
  }
  return area.label || area.geoid || 'Area';
}
